import { useTheme } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useCallback, useMemo } from 'react'

import { useBookingContext } from '@providers/booking'
import { i18WithDefault as t } from '@shared/locale'
import { checkInOutTimeIntervals, isCheckInTimeEnabled, isCheckOutTimeEnabled } from 'components-ui/src/shared'
import { Types } from 'service-api'
import { TCheckInOutProps } from './details_policies.d'

export const CheckInOut = ({ formik, reservation }: TCheckInOutProps) => {
  const bookingCtx = useBookingContext()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))

  const { startDate, endDate } = useMemo(() => {
    const invoice = reservation.order?.invoices?.find(({ items }) => {
      return items?.some(
        ({ locationServiceTypeId }) => locationServiceTypeId === reservation.order?.primaryServiceType?.id
      )
    })

    return invoice?.period?.[0] || {}
  }, [reservation.order])
  const checkInTimes = useMemo(
    () => checkInOutTimeIntervals(startDate, reservation.checkInHour as Types.RestrictionClientDto[]),
    [reservation.checkInHour, startDate]
  )
  const checkOutTimes = useMemo(
    () => checkInOutTimeIntervals(endDate, reservation.checkOutHour as Types.RestrictionClientDto[]),
    [reservation.checkOutHour, endDate]
  )

  const onChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ev: React.ChangeEvent<any>) => {
      const { name, id, value } = ev.target
      bookingCtx.dispatch({
        type: 'SET_CHECK_IN_OUT_FORM',
        payload: {
          value: {
            fieldName: name || id,
            value,
          },
        },
      })

      formik.handleChange(ev)
    },
    [bookingCtx, formik]
  )

  return (
    <Paper elevation={1} sx={{ p: 2, pb: 3, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
      <Typography variant="h5">{t('checkInOutForm.title')}</Typography>
      <Typography variant="body1" mt={3} mb={2}>
        {t('checkInOutForm.subtitle')}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }}>
        {isCheckInTimeEnabled(reservation.locationBookingConf!) && (
          <TextField
            id="checkInTime"
            name="checkInTime"
            label={t('checkInOutForm.checkIn')}
            variant="outlined"
            size="small"
            select
            sx={{ flexBasis: 0, flexGrow: 1, mr: { xs: 0, sm: 1 } }}
            value={formik.values.checkInTime}
            onChange={onChange}
            error={formik.touched.checkInTime && Boolean(formik.errors.checkInTime)}
            helperText={formik.touched.checkInTime && formik.errors.checkInTime}
          >
            {checkInTimes.map((ctime, i) => (
              <MenuItem key={`${ctime.ampm}-${i}`} value={ctime.timez}>
                {ctime.ampm}
              </MenuItem>
            ))}
          </TextField>
        )}
        {isCheckOutTimeEnabled(reservation.locationBookingConf!) && (
          <TextField
            id="checkOutTime"
            name="checkOutTime"
            label={t('checkInOutForm.checkOut')}
            variant="outlined"
            size="small"
            select
            sx={{ flexBasis: 0, flexGrow: 1, mt: { xs: 2, sm: 0 } }}
            value={formik.values.checkOutTime}
            onChange={onChange}
            error={formik.touched.checkOutTime && Boolean(formik.errors.checkOutTime)}
            helperText={formik.touched.checkOutTime && formik.errors.checkOutTime}
          >
            {checkOutTimes.map((ctime, i) => (
              <MenuItem key={`${ctime.ampm}-${i}`} value={ctime.timez}>
                {ctime.ampm}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
    </Paper>
  )
}
